/**
 * OS-level file associations — the single source of truth for which file
 * types the INSTALLED shells (PWA and the Tauri desktop app) register with
 * the operating system, so double-clicking a file on disk opens it here.
 *
 * Both OS manifests are GENERATED from this table by
 * `scripts/generate-os-manifests.ts` (`pnpm generate:os-manifests`):
 *
 *   - `public/manifest.json`         → `file_handlers` (+ `share_target` accept)
 *   - `src-tauri/tauri.conf.json`    → `bundle.fileAssociations`
 *
 * A drift-guard unit test regenerates both in memory and fails when a checked
 * in manifest no longer matches this table, so edits here can't silently rot.
 *
 * Rules for rows:
 *
 *   - Only formats with a WORKING loose-file open story are `enabled`. The OS
 *     hop is upload → `/w/<contentId>` → `resolveContentHandler`, so an
 *     enabled row must correspond to a system widget that declares the mime
 *     in its seeded `fileHandlers` (see scripts/seed-system-widgets.ts) —
 *     otherwise the OS would offer us as a handler and the open would
 *     dead-end after upload. The invariants test cross-checks this against
 *     DOCUMENT_FORMATS-adjacent expectations.
 *
 *   - ADR-0022 PROJECT formats (.deck, .crm, .eda, .sheets, .slides, .docs,
 *     .tracker, .guitarro) are deliberately ABSENT: in the cloud they are
 *     folders, not files. The `.deck`-style extension on disk is only the
 *     project's inner snapshot child (a user can download one), and
 *     re-uploading that loose child does not reconstruct a project — the
 *     export/import bundle path owns project round-trips.
 *
 *   - Registration is "Open With" CANDIDACY, never default-handler capture.
 *     macOS ranks by CFBundleTypeRole, Windows 10+ requires an explicit user
 *     choice, Chromium prompts on first PWA launch — nothing here silently
 *     steals `.pdf` or `.md` from the user's current editor.
 */

export interface OsFileAssociation {
  /** Dot-prefixed extensions, primary first. */
  exts: string[];
  /** The mime the uploaded row must carry for handler resolution. */
  mime: string;
  /** OS document-type name (Finder "Kind", Windows type column). */
  name: string;
  /** System widget that opens the mime (provenance; checked by tests). */
  handlerSlug: string;
  /** macOS CFBundleTypeRole. Viewer = opens but never rewrites the original. */
  role: 'Editor' | 'Viewer';
  /** Excluded from generated manifests when false; `reason` says why. */
  enabled: boolean;
  reason?: string;
}

export const OS_FILE_ASSOCIATIONS: OsFileAssociation[] = [
  // ── Office ────────────────────────────────────────────────────────────────
  {
    exts: ['.docx'],
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    name: 'Word document',
    handlerSlug: 'office-docs',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.pptx'],
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    name: 'PowerPoint presentation',
    handlerSlug: 'office-slides',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.xlsx'],
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'Excel workbook',
    handlerSlug: 'office-sheets',
    role: 'Editor',
    enabled: false,
    // Mirrors the seed: Workbook's xlsx reader/writer is still stubbed, so it
    // deliberately does not declare the mime yet. Flip this in the same change
    // that lands the engine + the seed's fileHandlers entry.
    reason: 'office-sheets does not declare the xlsx handler yet',
  },

  // ── Documents ─────────────────────────────────────────────────────────────
  {
    exts: ['.pdf'],
    mime: 'application/pdf',
    name: 'PDF document',
    handlerSlug: 'pdf',
    role: 'Viewer',
    enabled: true,
  },
  {
    exts: ['.md', '.markdown'],
    mime: 'text/markdown',
    name: 'Markdown document',
    handlerSlug: 'docs',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.txt'],
    mime: 'text/plain',
    name: 'Text document',
    handlerSlug: 'docs',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.svg'],
    mime: 'image/svg+xml',
    name: 'SVG image',
    handlerSlug: 'vector-editor',
    role: 'Editor',
    enabled: true,
    // Vector Design declares image/svg+xml for `open` and IMPORTS the file
    // on boot (collab-kit onOpenFile). Save writes a .design document and
    // leaves the original .svg untouched, so the OS hop no longer dead-ends.
  },
  {
    exts: ['.csv'],
    mime: 'text/csv',
    name: 'CSV file',
    handlerSlug: 'sheet',
    role: 'Editor',
    enabled: false,
    // Sheet takes .csv via drag-drop / its import menu, not the open registry.
    reason: 'no widget declares text/csv open',
  },

  // ── Robutler document formats (standalone files, not ADR-0022 projects) ──
  {
    exts: ['.board'],
    mime: 'application/vnd.robutler.board+json',
    name: 'Robutler whiteboard',
    handlerSlug: 'collab-board',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.design'],
    mime: 'application/vnd.robutler.design+json',
    name: 'Robutler design',
    handlerSlug: 'vector-editor',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.video'],
    mime: 'application/vnd.robutler.video+json',
    name: 'Robutler video project',
    handlerSlug: 'video-editor',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.video3d'],
    mime: 'application/vnd.robutler.video3d+json',
    name: 'Robutler 3D video project',
    handlerSlug: 'video-3d',
    role: 'Editor',
    enabled: true,
  },
  {
    exts: ['.robsheet'],
    mime: 'application/vnd.robutler.sheet+json',
    name: 'Robutler sheet',
    handlerSlug: 'sheet',
    role: 'Editor',
    enabled: true,
  },
];

export function enabledOsAssociations(): OsFileAssociation[] {
  return OS_FILE_ASSOCIATIONS.filter((a) => a.enabled);
}

/**
 * Mime for a filename per this table (enabled rows only). The OS hands the
 * browser an empty `File.type` for vendor extensions, so the open flow (and
 * the /share ingest) stamp the mime from the extension before uploading —
 * without it the row would land as a generic document no handler resolves.
 */
export function osAssociationForFilename(filename: string): OsFileAssociation | null {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = filename.slice(dot).toLowerCase();
  return enabledOsAssociations().find((a) => a.exts.includes(ext)) ?? null;
}

/** `file_handlers` fragment for public/manifest.json (Chromium installed PWA). */
export function pwaFileHandlers(): Array<{
  action: string;
  accept: Record<string, string[]>;
}> {
  return enabledOsAssociations().map((a) => ({
    action: '/open',
    accept: { [a.mime]: a.exts },
  }));
}

/**
 * `bundle.fileAssociations` fragment for src-tauri/tauri.conf.json.
 * Tauri wants extensions WITHOUT the leading dot; `role` feeds macOS
 * CFBundleTypeRole, `mimeType` the Linux .desktop entry.
 */
export function tauriFileAssociations(): Array<{
  ext: string[];
  name: string;
  description: string;
  role: 'Editor' | 'Viewer';
  mimeType: string;
}> {
  return enabledOsAssociations().map((a) => ({
    ext: a.exts.map((e) => e.replace(/^\./, '')),
    name: a.name,
    description: a.name,
    role: a.role,
    mimeType: a.mime,
  }));
}

/**
 * `share_target.params.files[0].accept` for public/manifest.json — media
 * wildcards (the original share sheet surface) plus every registered document
 * type, so sharing a .docx / .pdf into the installed app ingests instead of
 * being filtered out by the OS share sheet.
 */
export function shareTargetAccept(): string[] {
  const docMimes = enabledOsAssociations().map((a) => a.mime);
  const docExts = enabledOsAssociations().flatMap((a) => a.exts);
  return ['image/*', 'video/*', 'audio/*', ...docMimes, ...docExts];
}
